import {gql} from '@apollo/client';
import {Stack, Text} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {PlateSchema, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

import {
  FillPlateTemplateDetailsQuery,
  FillPlateTemplateDetailsQueryVariables,
} from './__generated/FillPlateTemplateDetailsQuery';
import PlateSizeSelector, {getPlateSizeLabel} from './PlateSizeSelector';

export const FILL_PLATE_TEMPLATE_DETAILS_QUERY = gql`
  query FillPlateTemplateDetailsQuery($templateId: StrictString!) @skipAudit @engComponent(name: PIPELINES) {
    plateDesignTemplate(id: $templateId) {
      ... on PlateDesignTemplateNode {
        id
        scratchPlate {
          ... on ScratchPlateNode {
            id
            height
            width
            plateSchema {
              ... on PlateSchemaNode {
                id
                name
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface TemplateDetails {
  height: number | null;
  plateSchema: {id: string; name: string} | null;
  schemaUnavailable: boolean;
  status: 'loading' | 'success' | 'unavailable';
  width: number | null;
}

export default function FillPlateSizeEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const templateId = typeof config.plateDesignTemplateId === 'string' ? config.plateDesignTemplateId : null;
  const templateResult = useSkippableBenchlingQuery<
    FillPlateTemplateDetailsQuery,
    FillPlateTemplateDetailsQueryVariables
  >(FILL_PLATE_TEMPLATE_DETAILS_QUERY, {
    skip: templateId == null,
    variables: {templateId: templateId ?? ''},
  });

  const templateDetails = useMemo<TemplateDetails>(() => {
    if (templateId == null) {
      return {
        height: null,
        plateSchema: null,
        schemaUnavailable: false,
        status: 'success',
        width: null,
      };
    }
    if (templateResult.type === QueryResultType.PENDING || templateResult.type === QueryResultType.SKIPPED) {
      return {
        height: null,
        plateSchema: null,
        schemaUnavailable: false,
        status: 'loading',
        width: null,
      };
    }
    if (templateResult.type === QueryResultType.ERROR) {
      return {
        height: null,
        plateSchema: null,
        schemaUnavailable: false,
        status: 'unavailable',
        width: null,
      };
    }
    const template = templateResult.data.plateDesignTemplate;
    if (isInaccessible(template) || isInaccessible(template.scratchPlate)) {
      return {
        height: null,
        plateSchema: null,
        schemaUnavailable: false,
        status: 'unavailable',
        width: null,
      };
    }
    const schema = template.scratchPlate.plateSchema;
    const schemaUnavailable = schema != null && isInaccessible(schema);
    return {
      height: template.scratchPlate.height,
      plateSchema:
        schema == null || schemaUnavailable || schema.__typename !== 'PlateSchemaNode'
          ? null
          : {id: schema.id, name: schema.name},
      schemaUnavailable,
      status: 'success',
      width: template.scratchPlate.width,
    };
  }, [templateId, templateResult]);

  const manualPlateHeight = typeof config.plateHeight === 'number' ? config.plateHeight : null;
  const manualPlateWidth = typeof config.plateWidth === 'number' ? config.plateWidth : null;
  const displayedHeight = templateId == null ? manualPlateHeight : templateDetails.height;
  const displayedWidth = templateId == null ? manualPlateWidth : templateDetails.width;
  const selectedPlateSchemaId =
    templateId == null
      ? typeof config.plateSchemaId === 'string'
        ? config.plateSchemaId
        : null
      : (templateDetails.plateSchema?.id ?? null);

  const parsedManualPlateSize = useMemo(
    (): [number, number] | null =>
      manualPlateHeight != null && manualPlateWidth != null ? [manualPlateHeight, manualPlateWidth] : null,
    [manualPlateHeight, manualPlateWidth]
  );
  const plateSchemaFilter = useCallback(
    (schema: PlateSchema): boolean =>
      parsedManualPlateSize != null &&
      schema.height === parsedManualPlateSize[0] &&
      schema.width === parsedManualPlateSize[1],
    [parsedManualPlateSize]
  );

  const templateDataIsPending = templateId != null && templateDetails.status === 'loading';
  const templateDataIsUnavailable = templateId != null && templateDetails.status === 'unavailable';
  const inheritedControlsDisabled = templateId != null;
  const schemaPlaceholder = templateDataIsPending
    ? 'Loading plate schema...'
    : templateDataIsUnavailable || templateDetails.schemaUnavailable
      ? 'Plate schema unavailable'
      : templateId != null && templateDetails.plateSchema == null
        ? 'No plate schema'
        : undefined;

  const plateSizeDisplayName = templateDataIsPending
    ? 'Loading plate size...'
    : templateDataIsUnavailable
      ? 'Plate size unavailable'
      : displayedHeight != null && displayedWidth != null
        ? getPlateSizeLabel(displayedHeight, displayedWidth)
        : 'No plate size';

  return (
    <Stack spacing="xs" {...getTestProps({component: 'FillPlateSizeEditor', element: 'root'})}>
      <PlateSizeSelector
        disabled={disabled || inheritedControlsDisabled}
        height={displayedHeight}
        displayName={plateSizeDisplayName}
        onChange={(height, width) => {
          let updatedConfig = setValueInConfig(config, ['plateHeight'], height);
          updatedConfig = setValueInConfig(updatedConfig, ['plateWidth'], width);
          updatedConfig = setValueInConfig(updatedConfig, ['plateSchemaId'], null);
          onChangeConfig(updatedConfig, ['plateHeight'], height);
        }}
        onClear={() => {
          let updatedConfig = setValueInConfig(config, ['plateHeight'], null);
          updatedConfig = setValueInConfig(updatedConfig, ['plateWidth'], null);
          updatedConfig = setValueInConfig(updatedConfig, ['plateSchemaId'], null);
          onChangeConfig(updatedConfig, ['plateHeight'], null);
        }}
        width={displayedWidth}
      />
      <Stack spacing={4}>
        <Text fz="sm" fw={600}>
          Plate schema
        </Text>
        <PlateSchemaSelector
          compact={true}
          displayedPlateSchema={templateId == null ? undefined : templateDetails.plateSchema}
          plateSchemaType={PlateSchemaType.FIXED_PLATE}
          plateSchemaFilter={parsedManualPlateSize == null ? undefined : plateSchemaFilter}
          onChange={(schema) => {
            onChangeConfig(config, ['plateSchemaId'], schema?.id ?? null);
          }}
          placeholder={schemaPlaceholder}
          registryId={undefined}
          selectedPlateSchemaId={selectedPlateSchemaId}
          disabled={disabled || inheritedControlsDisabled || parsedManualPlateSize == null}
          disabledMessage={
            templateDataIsPending
              ? 'Loading plate design template'
              : templateDataIsUnavailable || templateDetails.schemaUnavailable
                ? 'Plate design template details are unavailable'
                : parsedManualPlateSize == null && !inheritedControlsDisabled
                  ? 'Select a plate size first'
                  : undefined
          }
          shouldShowNoSchemaOption={true}
        />
      </Stack>
    </Stack>
  );
}
